import { Form, Offcanvas } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import { quillImageHandler } from "../../utility/quilImageHandler";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface AddTermConditionProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

const AddTermCondition = ({
  show,
  handleClose,
  data,
}: AddTermConditionProps) => {
  const {
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange",
  });

  const [isLoading, setLoading] = useState(false);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const quillRef = useRef<any>(null);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
  }>({});

  useEffect(() => {
    if (data && show) {
      setDescription(data.description || "");
      setBannerFile(null);
    }
  }, [data, show, reset]);
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const newErrors: any = {};

    if (isQuillEmpty(description)) {
      newErrors.description = "Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return !newErrors.description;
  };
  const onSubmit = async (formValues: FormData) => {
    if (!description || description.trim() === "") {
      toast.error("Description is required");
      return;
    }
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);

    const formData = new FormData();

    const editor = quillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html);

    if (bannerFile) {
      formData.append("image", bannerFile);
    }

    if (data?._id) {
      formData.append("id", data._id);
    }

    WebService.fileUploadAPI({
      action: "term-condition/save",
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message || "Saved successfully");
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "Something went wrong");
      });
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Term And Condition</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* IMAGE */}
          <Form.Group className="mb-3">
            <Form.Label className="label">Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              className="custom-image-upload"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) setBannerFile(file);
              }}
            />
            <div className="text-primary mt-1" style={{ fontSize: 13 }}>
              Note: Image size <strong>590 × 445 px</strong> ( png, jpg, jpeg,
              svg)
            </div>
          </Form.Group>

          {(bannerFile || data?.image) && (
            <div className="mb-3 text-center">
              <img
                src={bannerFile ? URL.createObjectURL(bannerFile) : data?.image}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                }}
              />
            </div>
          )}

          {/* DESCRIPTION */}
          <Form.Group className="mb-3">
            <Form.Label className="label">
              Description <span className="text-danger">*</span>
            </Form.Label>

            <CommonQuill
              ref={quillRef}
              value={description}
              onChange={setDescription}
              storage="TERM_AND_CONDITION"
              initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
              onClearError={() =>
                setCustomErrors((p) => ({
                  ...p,
                  description: undefined,
                }))
              }
            />
            {customErrors.description && (
              <div className="error-message">{customErrors.description}</div>
            )}
          </Form.Group>

          {/* SAVE */}
          <div className="d-flex justify-content-end">
            <CustomButton
              type="submit"
              isLoading={isLoading}
              label="Save"
              className="w-100"
            />
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddTermCondition;
